import { useEffect, useMemo, useState, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import {
  MenuItem,
  Select as MuiSelect,
  Tooltip,
  styled,
  IconButton,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from '../../../../components/buttons'
import { TextInput } from '../../../../components/inputs/TextInput'
import { NumberTextInput } from '../../../../components/inputs/NumberTextInput'
import { DateInput } from '../../../../components/inputs/DatePicker'
import { Text, Title } from '../../../../components/typography'
import { colorPalette } from '../../../../style/colorPalette'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { intl } from '../../../../../i18n'
import {
  GeneratedSprint,
  GenerateSprintsParams,
  SprintInterval,
  appendSprints,
  generateSprints,
  shiftSprintDates,
} from './generateSprints'

type Props = {
  open: boolean
  onClose: () => void
  onAdd: (sprints: GeneratedSprint[]) => void
  charactersAllowed?: string
}

const INTERVALS: { value: SprintInterval; labelId: string }[] = [
  { value: '1w', labelId: 'bulkSprint.interval.1w' },
  { value: '2w', labelId: 'bulkSprint.interval.2w' },
  { value: '1month', labelId: 'bulkSprint.interval.1month' },
]

const MAX_COUNT = 52
const MIN_COUNT = 1

const BulkSprintDialog = ({
  open,
  onClose,
  onAdd,
  charactersAllowed,
}: Props) => {
  const [interval, setInterval] = useState<SprintInterval>('1w')
  const [startDate, setStartDate] = useState<Date | undefined>(new Date())
  const [countInput, setCountInput] = useState<string>('2')
  const [prefix, setPrefix] = useState<string>('Sprint')
  const [preview, setPreview] = useState<GeneratedSprint[]>([])

  useEffect(() => {
    if (open) {
      setInterval('1w')
      setStartDate(new Date())
      setCountInput('2')
      setPrefix('Sprint')
      setPreview([])
    }
  }, [open])

  const isWeekInterval = interval === '1w' || interval === '2w'
  const startDayOfMonth = startDate ? startDate.getDate() : 1

  const prefixError = useMemo(() => {
    if (prefix.length === 0 || !charactersAllowed) return undefined
    const match = prefix.match(`^[${charactersAllowed}]*$`)
    if (!match) {
      return (
        intl.formatMessage({ id: 'validator.invalidCharacter.allowed' }) +
        charactersAllowed
      )
    }
    return undefined
  }, [prefix, charactersAllowed])

  const count = useMemo(() => {
    const v = parseInt(countInput, 10)
    if (isNaN(v) || v < MIN_COUNT) return 0
    return Math.min(v, MAX_COUNT)
  }, [countInput])

  const buildParams = useCallback(
    (targetCount: number): GenerateSprintsParams => {
      const dateStr = dateV2Service.format(startDate!)
      return isWeekInterval
        ? { interval, startDate: dateStr, count: targetCount, prefix }
        : {
            interval: '1month',
            startDayOfMonth,
            startDate: dateStr,
            count: targetCount,
            prefix,
          }
    },
    [interval, isWeekInterval, startDate, startDayOfMonth, prefix]
  )

  // interval, startDate, prefix が変わったら全再生成
  useEffect(() => {
    if (!startDate || prefixError || count <= 0) {
      setPreview([])
      return
    }
    setPreview(generateSprints(buildParams(count)))
    // count は別の useEffect で差分管理するため依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildParams, prefixError])

  // count が変わったら末尾を追加/削除（調整を保持）
  useEffect(() => {
    if (!startDate || prefixError) return
    setPreview(prev => {
      if (count <= 0) return []
      if (count === prev.length) return prev
      if (count < prev.length) return prev.slice(0, count)
      if (prev.length === 0) return generateSprints(buildParams(count))
      return appendSprints(prev, count - prev.length, interval, prefix)
    })
  }, [count, startDate, prefixError, interval, prefix, buildParams])

  const emptyFieldMessage = useMemo(() => {
    if (preview.length > 0) return undefined
    const missing: string[] = []
    if (!startDate) {
      missing.push(intl.formatMessage({ id: 'bulkSprint.startDate' }))
    }
    if (count <= 0) {
      missing.push(intl.formatMessage({ id: 'bulkSprint.count' }))
    }
    if (prefix.length === 0) {
      missing.push(intl.formatMessage({ id: 'bulkSprint.prefix' }))
    }
    if (missing.length === 0) return undefined
    return intl.formatMessage(
      { id: 'bulkSprint.preview.empty' },
      {
        fields: missing.join(
          intl.formatMessage({ id: 'bulkSprint.preview.empty.separator' })
        ),
      }
    )
  }, [preview.length, startDate, count, prefix])

  const handleAdd = useCallback(() => {
    if (preview.length === 0) return
    onAdd(preview)
    onClose()
  }, [preview, onAdd, onClose])

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCountInput(e.target.value)
  }

  const handleCountStep = useCallback((step: 1 | -1) => {
    setCountInput(prev => {
      const v = parseInt(prev, 10)
      if (isNaN(v)) return String(MIN_COUNT)
      return String(Math.max(MIN_COUNT, Math.min(MAX_COUNT, v + step)))
    })
  }, [])

  const handleShift = useCallback(
    (index: number, direction: 1 | -1) => {
      setPreview(prev => {
        if (prev.length === 0) return prev
        return shiftSprintDates(prev, index, direction * 7, prefix)
      })
    },
    [prefix]
  )

  const hasError = !!prefixError

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth={true}
      PaperProps={{
        sx: { height: '640px', borderRadius: '8px', overflow: 'hidden' },
      }}
    >
      <StyledDialogTitle>
        <Title>{intl.formatMessage({ id: 'bulkSprint.title' })}</Title>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>
      <DialogContent
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        <FormSection>
          <FormRow>
            <FormLabel>
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.interval' })}
              </Text>
            </FormLabel>
            <StyledMuiSelect
              value={interval}
              onChange={e => setInterval(e.target.value as SprintInterval)}
              size="small"
            >
              {INTERVALS.map(({ value, labelId }) => (
                <MenuItem key={value} value={value}>
                  {intl.formatMessage({ id: labelId })}
                </MenuItem>
              ))}
            </StyledMuiSelect>
          </FormRow>

          <FormRow>
            <FormLabel>
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.startDate' })}
              </Text>
            </FormLabel>
            <InputBox>
              <DateInput
                value={startDate}
                onChange={v => setStartDate(v)}
                placeholder="YYYY/MM/DD"
              />
            </InputBox>
          </FormRow>

          <FormRow>
            <FormLabel>
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.count' })}
              </Text>
            </FormLabel>
            <CountControl>
              <StepButton onClick={() => handleCountStep(-1)}>-</StepButton>
              <InputBox className="center" style={{ width: '60px' }}>
                <NumberTextInput
                  value={countInput}
                  onChange={handleCountChange}
                  inputProps={{
                    min: MIN_COUNT,
                    max: MAX_COUNT,
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.select(),
                  }}
                />
              </InputBox>
              <StepButton onClick={() => handleCountStep(1)}>+</StepButton>
            </CountControl>
            <Text variant="s">
              {intl.formatMessage({ id: 'bulkSprint.countUnit' })}
            </Text>
          </FormRow>

          <FormRow>
            <FormLabel>
              <Text variant="sBold">
                {intl.formatMessage({ id: 'bulkSprint.prefix' })}
              </Text>
            </FormLabel>
            <div>
              <InputBox
                style={{ width: '200px' }}
                className={prefixError ? 'error' : undefined}
              >
                <TextInput
                  value={prefix}
                  onChange={e => setPrefix(e.target.value)}
                  onFocus={e => e.target.select()}
                  placeholder="Sprint"
                  fullWidth={true}
                />
              </InputBox>
              {prefixError && (
                <ErrorText>
                  <Text variant="2s">{prefixError}</Text>
                </ErrorText>
              )}
            </div>
          </FormRow>
        </FormSection>

        <PreviewSection>
          <Text variant="sBold">
            {intl.formatMessage({ id: 'bulkSprint.preview' })}
          </Text>
          <PreviewScrollArea>
            <PreviewTable>
              <thead>
                <PreviewHeaderRow>
                  <PreviewTh>
                    <Text variant="2sBold">
                      {intl.formatMessage({
                        id: 'bulkSprint.preview.name',
                      })}
                    </Text>
                  </PreviewTh>
                  <PreviewTh>
                    <Text variant="2sBold">
                      {intl.formatMessage({
                        id: 'bulkSprint.preview.startDate',
                      })}
                    </Text>
                  </PreviewTh>
                  <PreviewTh>
                    <Text variant="2sBold">
                      {intl.formatMessage({
                        id: 'bulkSprint.preview.endDate',
                      })}
                    </Text>
                  </PreviewTh>
                  <PreviewTh style={{ width: '80px' }}>
                    <Text variant="2sBold">
                      {intl.formatMessage({
                        id: 'bulkSprint.preview.adjust',
                      })}
                    </Text>
                  </PreviewTh>
                </PreviewHeaderRow>
              </thead>
              <tbody>
                {preview.map((sprint, i) => (
                  <PreviewRow key={sprint.name}>
                    <PreviewTd>
                      <Text variant="2s">{sprint.name}</Text>
                    </PreviewTd>
                    <PreviewTd>
                      <Text variant="2s">
                        {dateV2Service.formatWithWeekday(sprint.startDate)}
                      </Text>
                    </PreviewTd>
                    <PreviewTd>
                      <Text variant="2s">
                        {dateV2Service.formatWithWeekday(sprint.endDate)}
                      </Text>
                    </PreviewTd>
                    <PreviewTd>
                      <ShiftButtonGroup>
                        <Tooltip
                          title={intl.formatMessage({
                            id: 'bulkSprint.shift.backward',
                          })}
                          disableInteractive={true}
                          slotProps={{
                            tooltip: { sx: { whiteSpace: 'pre-line' } },
                            popper: { sx: { pointerEvents: 'none' } },
                          }}
                        >
                          <ShiftButton onClick={() => handleShift(i, -1)}>
                            -1w
                          </ShiftButton>
                        </Tooltip>
                        <Tooltip
                          title={intl.formatMessage({
                            id: 'bulkSprint.shift.forward',
                          })}
                          disableInteractive={true}
                          slotProps={{
                            tooltip: { sx: { whiteSpace: 'pre-line' } },
                            popper: { sx: { pointerEvents: 'none' } },
                          }}
                        >
                          <ShiftButton onClick={() => handleShift(i, 1)}>
                            +1w
                          </ShiftButton>
                        </Tooltip>
                      </ShiftButtonGroup>
                    </PreviewTd>
                  </PreviewRow>
                ))}
              </tbody>
            </PreviewTable>
            {emptyFieldMessage && (
              <EmptyMessage>
                <Text variant="s">{emptyFieldMessage}</Text>
              </EmptyMessage>
            )}
          </PreviewScrollArea>
        </PreviewSection>
      </DialogContent>
      <StyledDialogActions>
        <Button colorPattern="monotone" variant="outlined" onClick={onClose}>
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
        <Button
          colorPattern="skyBlue"
          variant="filled"
          onClick={handleAdd}
          disabled={preview.length === 0 || hasError}
        >
          {intl.formatMessage({ id: 'bulkSprint.add' })}
        </Button>
      </StyledDialogActions>
    </Dialog>
  )
}

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  padding: '12px 16px',
})

const FormSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '20px 24px',
  backgroundColor: colorPalette.skyBlue[0],
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  flexShrink: 0,
})

const FormRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const FormLabel = styled('div')({
  width: '120px',
  flexShrink: 0,
})

const StyledMuiSelect = styled(MuiSelect)({
  minWidth: '140px',
  fontSize: '13px',
  backgroundColor: colorPalette.monotone[0],
})

const InputBox = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '4px 8px',
  fontSize: '13px',
  backgroundColor: colorPalette.monotone[0],
  '&:focus-within': {
    borderColor: colorPalette.skyBlue[5],
  },
  '&.error': {
    borderColor: colorPalette.pink[5],
  },
  '&.center': {
    textAlign: 'center',
  },
  '& input': {
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    width: '100%',
    padding: 0,
  },
  '&.center input': {
    textAlign: 'center',
  },
})

const CountControl = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const StepButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  border: `1px solid ${colorPalette.skyBlue[2]}`,
  borderRadius: '4px',
  backgroundColor: colorPalette.monotone[0],
  fontSize: '16px',
  cursor: 'pointer',
  color: colorPalette.skyBlue[7],
  fontWeight: 600,
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[0],
    borderColor: colorPalette.skyBlue[5],
  },
})

const ErrorText = styled('div')({
  color: colorPalette.pink[5],
  marginTop: '4px',
})

const PreviewSection = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 24px 0',
})

const PreviewScrollArea = styled('div')({
  marginTop: '8px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
})

const PreviewTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '6px',
  overflow: 'hidden',
})

const PreviewHeaderRow = styled('tr')({
  backgroundColor: colorPalette.skyBlue[1],
  borderBottom: `2px solid ${colorPalette.skyBlue[2]}`,
})

const PreviewTh = styled('th')({
  textAlign: 'left',
  padding: '6px 8px',
  position: 'sticky',
  top: 0,
  backgroundColor: colorPalette.skyBlue[1],
  zIndex: 1,
  color: colorPalette.skyBlue[10],
})

const PreviewRow = styled('tr')({
  borderBottom: `1px solid ${colorPalette.monotone[1]}`,
  '&:nth-of-type(odd)': {
    backgroundColor: colorPalette.skyBlue[0],
  },
})

const PreviewTd = styled('td')({
  padding: '4px 8px',
})

const ShiftButtonGroup = styled('div')({
  display: 'flex',
  gap: '4px',
})

const ShiftButton = styled('button')({
  border: `1px solid ${colorPalette.skyBlue[2]}`,
  borderRadius: '4px',
  backgroundColor: colorPalette.monotone[0],
  padding: '2px 6px',
  fontSize: '11px',
  cursor: 'pointer',
  color: colorPalette.skyBlue[7],
  fontWeight: 600,
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[0],
    borderColor: colorPalette.skyBlue[5],
  },
})

const StyledDialogActions = styled(DialogActions)({
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
  padding: '12px 24px',
})

const EmptyMessage = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  color: colorPalette.monotone[3],
})

export { BulkSprintDialog }
